import type { Filter, FilterDataOption, FilterDataMultiSelect } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { AccommodationThemes } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { type OPTIONS_SORT, sortOptions } from '@/app/common/filters/types/shared/utils/sort.ts';
import { counterOf, disabledAtZero, keysWithSelected } from '@/app/common/filters/groups/optionCounts.ts';

type CreateRatingFilterOptions = {
  selectedValues: string[];
  accommodationThemes: AccommodationThemes;
  translationDictionary: TranslationDictionary;
};
const createPropertyStyleFilterOptions = ({
  selectedValues,
  accommodationThemes,
  translationDictionary,
}: CreateRatingFilterOptions): FilterDataOption[] => {
  const names = keysWithSelected(Object.keys(accommodationThemes), selectedValues);

  return names
    .map((accommodationThemeName) => {
      const selected = selectedValues.includes(accommodationThemeName);
      const counter = counterOf(accommodationThemes, accommodationThemeName);
      return {
        label: getTranslationServer(
          `vacation.search.filters.trustyou.options.${accommodationThemeName}`,
          translationDictionary
        ),
        selected,
        disabled: disabledAtZero(selected, counter),
        value: accommodationThemeName,
        counter,
      };
    })
    .filter((option) => !!option.label);
};

type CreatePropertyStyleFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
  optionsSort?: OPTIONS_SORT;
};
export const createPropertyStyleFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
  optionsSort,
}: CreatePropertyStyleFilters): Filter[] => {
  if (!dynamicFiltersResponse?.accommodationThemes) return [];

  const selectedValues = searchParams[URLParameters.ACCOMODATION_THEME]?.split(',') || [];

  const rawOptions = createPropertyStyleFilterOptions({
    selectedValues,
    accommodationThemes: dynamicFiltersResponse.accommodationThemes,
    translationDictionary,
  });
  const options = optionsSort !== undefined ? sortOptions(optionsSort, rawOptions) : rawOptions;
  if (!options.length) return [];

  return [
    {
      name: FILTER_NAME.PROPERTY_STYLE,
      type: FILTER_TYPE.MULTI_SELECT,
      data: { options } as FilterDataMultiSelect,
    },
  ];
};
